import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useLoginUser,
  useRegisterUser,
  useGoogleAuth,
  useCities,
  useForgotPassword,
  useVerifyOtp,
  useResetPassword,
} from '../../api';
import { useServicesLocation } from '../../hooks/useServicesLocation';
import { useSelector, useDispatch } from 'react-redux';
import {
  MapPin,
  ShoppingBag,
  Menu as MenuIcon,
  X as XIcon,
  User as UserIcon,
  Calendar,
  LogOut,
  ChevronDown,
} from 'lucide-react';
import { login as loginAction, logout as logoutAction } from '../../redux/slice/authSlice';
import { userRoles } from '../../constants/userRoles';
import styles from './usernav.module.css';
import { Button, Input } from 'glintly-ui';
import { homeGenderPath, resolveHomeGender } from '../../utils/homeGender';
import { AUTH_MODAL_EVENT } from '../../utils/openAuthModal';
import BrandLogo from '../brand/BrandLogo';

const UserNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authType, setAuthType] = useState('signin');
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const cartCount = useSelector((state) => state.cart.items.length);

  const { openLocationModal, usedGeolocation, selectedCityName, preference, geoAttemptDone } =
    useServicesLocation();

  const locationLabel = !geoAttemptDone
    ? 'Location…'
    : selectedCityName
      ? selectedCityName
      : preference?.mode === 'all'
        ? 'All areas'
        : usedGeolocation
          ? 'Current location'
          : 'Set location';

  const homeGender = resolveHomeGender(location.search);
  const focusedButton = homeGender === 'female' ? 'Female' : 'Male';
  const homePath = homeGenderPath(homeGender);

  const handleAuthClick = (type) => {
    setAuthType(type.toLowerCase());
    setShowAuthModal(true);
  };

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
  };

  const handleGenderClick = (gender) => {
    closeMenu();
    navigate(homeGenderPath(gender === 'Female' ? 'female' : 'male'));
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const newOpacity = Math.max(0.99, 1 - scrollY / 500);
      setOpacity(newOpacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onOpenAuth = (event) => {
      const requested = String(event.detail?.type || 'signin').toLowerCase();
      setAuthType(requested === 'signup' || requested === 'forgot' ? requested : 'signin');
      setShowAuthModal(true);
      setIsMenuOpen(false);
    };
    window.addEventListener(AUTH_MODAL_EVENT, onOpenAuth);
    return () => window.removeEventListener(AUTH_MODAL_EVENT, onOpenAuth);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const mq = window.matchMedia('(max-width: 1100px)');
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    const onViewportChange = (event) => {
      if (!event.matches) closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    mq.addEventListener('change', onViewportChange);
    if (mq.matches) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      mq.removeEventListener('change', onViewportChange);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Check if current route is dashboard
  const isDashboardRoute =
    location.pathname.startsWith('/admin') || location.pathname.startsWith('/vendor');

  // Don't render user navbar on dashboard routes
  if (isDashboardRoute) {
    return null;
  }

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/salons', label: 'Salons' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <div className={styles.appContainer}>
      <nav className={styles.barbarNavbar} style={{ opacity }}>
        <Link to={homePath} className={styles.barbarLogo} aria-label="B4BARBER home">
          <BrandLogo variant="lockup" tone="onLight" />
        </Link>
        <button
          className={styles.barbarMenuToggle}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-nav-drawer"
        >
          {isMenuOpen ? <XIcon size={20} /> : <MenuIcon size={20} />}
        </button>

        {isMenuOpen ? (
          <button
            type="button"
            className={styles.menuBackdrop}
            aria-label="Close menu"
            onClick={closeMenu}
          />
        ) : null}

        <div
          id="mobile-nav-drawer"
          className={`${styles.menuDrawer} ${isMenuOpen ? styles.menuDrawerOpen : ''}`}
          aria-hidden={!isMenuOpen}
        >
          <div className={styles.drawerHeader}>
            <span className={styles.drawerTitle}>Navigation</span>
            <button
              type="button"
              className={styles.drawerClose}
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <XIcon size={18} />
            </button>
          </div>
          <ul className={styles.barbarNavLinks}>
            {navLinks.map((link) => {
              const to = link.path === '/' ? homePath : link.path;
              return (
                <li
                  key={link.path}
                  className={location.pathname === link.path ? styles.barbarActiveNav : ''}
                >
                  <Link to={to} onClick={closeMenu}>
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className={styles.barbarNavButtons}>
            <button
              type="button"
              className={styles.locationBtn}
              onClick={() => {
                closeMenu();
                openLocationModal();
              }}
              title={
                selectedCityName
                  ? `Location: ${selectedCityName}`
                  : 'Choose city, search map, or paste a maps link'
              }
            >
              <MapPin size={15} style={{ color: '#dc2626', flexShrink: 0 }} />
              <span className={styles.locationBtnLabel}>{locationLabel}</span>
            </button>
            <div className={styles.barbarGenderToggle}>
              <button
                className={`${styles.barbarGenderBtn} ${
                  focusedButton === 'Male' ? styles.barbarFocused : ''
                }`}
                onClick={() => handleGenderClick('Male')}
              >
                Men
              </button>
              <button
                className={`${styles.barbarGenderBtn} ${
                  focusedButton === 'Female' ? styles.barbarFocused : ''
                }`}
                onClick={() => handleGenderClick('Female')}
              >
                Women
              </button>
            </div>
            <div>
              <Link to="/cart" className={styles.cartLink} aria-label="Cart" onClick={closeMenu}>
                <ShoppingBag size={18} />
                {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
              </Link>
            </div>
            {!user ? (
              <div className={styles.barbarAuthButtons}>
                <button
                  className={`${styles.barbarAuthBtn} ${styles.barbarSignin}`}
                  onClick={() => {
                    closeMenu();
                    handleAuthClick('SignIn');
                  }}
                >
                  Sign In
                </button>
                <button
                  className={`${styles.barbarAuthBtn} ${styles.barbarSignup}`}
                  onClick={() => {
                    closeMenu();
                    handleAuthClick('SignUp');
                  }}
                >
                  Register
                </button>
              </div>
            ) : (
              <div className={styles.userMenuWrapper}>
                <button
                  className={styles.userBtn}
                  onClick={() => setShowUserMenu((v) => !v)}
                  aria-label="User menu"
                >
                  <div className={styles.userAvatar}>
                    {(user.firstName || user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.userName}>
                    {user.firstName || user.name || 'Account'}
                  </span>
                  <ChevronDown size={14} style={{ color: '#64748b' }} />
                </button>

                {showUserMenu && (
                  <div className={styles.userDropdown}>
                    <div
                      style={{
                        padding: '0.5rem 0.65rem 0.4rem',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                        {user.firstName || user.name || 'User'}
                      </div>
                      <div
                        style={{
                          fontSize: '0.725rem',
                          color: '#64748b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.email}
                      </div>
                    </div>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        setShowUserMenu(false);
                        closeMenu();
                        navigate('/profile');
                      }}
                    >
                      <UserIcon size={14} />
                      <span>My Profile</span>
                    </button>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        setShowUserMenu(false);
                        closeMenu();
                        navigate('/bookings');
                      }}
                    >
                      <Calendar size={14} />
                      <span>My Bookings</span>
                    </button>
                    <div className={styles.dropdownDivider} />
                    <button
                      className={styles.dropdownItem}
                      style={{ color: '#dc2626' }}
                      onClick={() => {
                        localStorage.removeItem('token');
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('user');
                        dispatch(logoutAction());
                        setShowUserMenu(false);
                        closeMenu();
                        navigate('/');
                        toast.success('Logged out successfully');
                      }}
                    >
                      <LogOut size={14} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {showAuthModal && (
        <AuthModal
          authType={authType}
          setAuthType={setAuthType}
          onClose={() => setShowAuthModal(false)}
          setShowAuthModal={setShowAuthModal}
        />
      )}
    </div>
  );
};

const AuthModal = ({ authType, setAuthType, onClose, setShowAuthModal }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    name: '',
    secMobile: '',
    fullAddress: '',
    landmark: '',
    city: '',
    postalCode: '',
    country: 'India',
    profileImg: '',
    otp: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [useMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Sub-step within authType === 'forgot': request OTP -> verify it -> set a new password
  const [forgotStep, setForgotStep] = useState('email');
  const [resetToken, setResetToken] = useState('');
  const dispatch = useDispatch();
  const loginMutation = useLoginUser();
  const signupMutation = useRegisterUser();
  const googleAuthMutation = useGoogleAuth();
  const forgotPasswordMutation = useForgotPassword();
  const verifyOtpMutation = useVerifyOtp();
  const resetPasswordMutation = useResetPassword();

  // Reset the forgot-password sub-flow whenever it's left (back to sign in, or modal reopened)
  useEffect(() => {
    if (authType !== 'forgot') {
      setForgotStep('email');
      setResetToken('');
    }
  }, [authType]);

  // Fetch cities from API
  const { data: cities = [], isLoading: citiesLoading, error: citiesError } = useCities();

  const cityList = Array.isArray(cities) ? cities : [];

  const cityOptions = [
    {
      label: citiesLoading
        ? 'Loading cities…'
        : cityList.length === 0
          ? 'No cities in directory (optional)'
          : 'Select your city (optional)',
      value: '',
    },
    ...cityList
      .filter((city) => city && city.cityName != null && city._id != null)
      .map((city) => ({
        label: city.cityName,
        value: String(city._id),
      })),
  ];

  // Handle cities API error
  useEffect(() => {
    if (citiesError) {
      console.error('Failed to fetch cities:', citiesError);
      toast.error('Failed to load cities. Please try again.');
    }
  }, [citiesError]);

  const handleInputChange = (e) => {
    const target = e?.target;
    const name = target?.name;
    const value = target && 'value' in target ? target.value : typeof e === 'string' ? e : '';
    if (!name) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (authType === 'signin') {
      if (!formData.password || (!formData.email && !formData.mobile)) {
        setError('Please enter your ' + (useMobile ? 'mobile' : 'email') + ' and password.');
        return;
      }
      setLoading(true);
      loginMutation.mutate(
        {
          email: useMobile ? undefined : formData.email,
          mobile: useMobile ? formData.mobile : undefined,
          password: formData.password,
        },
        {
          onSuccess: (data) => {
            if (data.token) {
              localStorage.setItem('authToken', data.token);
            }
            if (data.user) {
              // Fix: Pass both user and token to loginAction
              dispatch(loginAction({ user: data.user, token: data.token }));
              localStorage.setItem('user', JSON.stringify(data.user));
            }
            toast.success('Login successful!');
            onClose();
            setFormData({
              email: '',
              mobile: '',
              password: '',
              name: '',
              secMobile: '',
              fullAddress: '',
              landmark: '',
              city: '',
              postalCode: '',
              country: 'India',
              profileImg: '',
            });
            setLoading(false);
          },
          onError: (error) => {
            setError(error.response?.data?.message || 'Login failed');
            setLoading(false);
          },
        }
      );
    } else if (authType === 'signup') {
      const missingFields = [];
      if (!formData.name) missingFields.push('Name');
      if (!formData.email && !formData.mobile) missingFields.push('Email or Mobile');
      if (!formData.password) missingFields.push('Password');
      if (!formData.confirmPassword) missingFields.push('Confirm Password');

      if (missingFields.length > 0) {
        setError(`Please fill all required fields: ${missingFields.join(', ')}`);
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      setLoading(true);
      signupMutation.mutate(
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password,
          role: userRoles.user,
          secMobile: formData.secMobile || undefined,
          address: {
            fullAddress: formData.fullAddress || undefined,
            landmark: formData.landmark || undefined,
            city: formData.city || undefined,
            postalCode: formData.postalCode || undefined,
            country: formData.country || 'India',
          },
          profileImg: formData.profileImg || undefined,
        },
        {
          onSuccess: (data) => {
            toast.success('Account created successfully!');
            onClose();
            setFormData({
              email: '',
              mobile: '',
              password: '',
              name: '',
              secMobile: '',
              fullAddress: '',
              landmark: '',
              city: '',
              postalCode: '',
              country: 'India',
              profileImg: '',
            });
            setLoading(false);
            // Auto login after signup
            setAuthType('signin');
            setShowAuthModal(true);
          },
          onError: (error) => {
            setError(error.response?.data?.message || 'Signup failed');
            setLoading(false);
          },
        }
      );
    } else if (authType === 'forgot') {
      if (forgotStep === 'email') {
        if (!formData.email) {
          setError('Please enter your email.');
          return;
        }
        setLoading(true);
        forgotPasswordMutation.mutate(
          { email: formData.email },
          {
            onSuccess: () => {
              toast.success('OTP sent to your email.');
              setForgotStep('otp');
              setLoading(false);
            },
            onError: (err) => {
              setError(err.response?.data?.error || 'Could not send OTP.');
              setLoading(false);
            },
          }
        );
      } else if (forgotStep === 'otp') {
        if (!formData.otp) {
          setError('Please enter the OTP sent to your email.');
          return;
        }
        setLoading(true);
        verifyOtpMutation.mutate(
          { email: formData.email, otp: formData.otp },
          {
            onSuccess: (data) => {
              setResetToken(data.resetToken);
              setForgotStep('reset');
              setLoading(false);
            },
            onError: (err) => {
              setError(err.response?.data?.error || 'Invalid or expired OTP.');
              setLoading(false);
            },
          }
        );
      } else if (forgotStep === 'reset') {
        if (!formData.newPassword || !formData.confirmNewPassword) {
          setError('Please enter and confirm your new password.');
          return;
        }
        if (formData.newPassword !== formData.confirmNewPassword) {
          setError('Passwords do not match.');
          return;
        }
        setLoading(true);
        resetPasswordMutation.mutate(
          { resetToken, newPassword: formData.newPassword },
          {
            onSuccess: () => {
              toast.success('Password reset successful. Please sign in.');
              setLoading(false);
              setAuthType('signin');
              setFormData((prev) => ({
                ...prev,
                password: '',
                otp: '',
                newPassword: '',
                confirmNewPassword: '',
              }));
            },
            onError: (err) => {
              setError(err.response?.data?.error || 'Could not reset password.');
              setLoading(false);
            },
          }
        );
      }
    }
  };
  const handleGoogleAuth = () => {
    // Debug: Check if Google Client ID is configured
    const clientId =
      process.env.REACT_APP_GOOGLE_CLIENT_ID ||
      '484051376347-bo6scg0cmdqpic9vf1hgrodjkqs6v4mb.apps.googleusercontent.com';
    console.log('Google Client ID:', clientId ? 'Configured' : 'Not configured');
    console.log('Client ID value:', clientId);

    if (!clientId || clientId === 'your-google-client-id') {
      toast.error('Google Client ID not configured. Please check the setup guide.');
      return;
    }

    // Load Google Identity Services library if not already loaded
    if (!window.google) {
      // Load Google Identity Services script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      script.onerror = () => {
        toast.error('Failed to load Google authentication service');
        setLoading(false);
      };
      document.head.appendChild(script);
    } else {
      initializeGoogleAuth();
    }
  };

  const initializeGoogleAuth = () => {
    try {
      if (window.google && window.google.accounts) {
        const clientId =
          process.env.REACT_APP_GOOGLE_CLIENT_ID ||
          '484051376347-bo6scg0cmdqpic9vf1hgrodjkqs6v4mb.apps.googleusercontent.com';
        // Check if Google Client ID is configured
        if (!clientId || clientId === 'your-google-client-id') {
          toast.error('Google Client ID not configured. Please check your environment variables.');
          setLoading(false);
          return;
        }

        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'email profile',
          ux_mode: 'popup',
          callback: handleGoogleCallback,
        });

        client.requestCode();
      } else {
        toast.error('Google authentication service not available');
        setLoading(false);
      }
    } catch (error) {
      console.error('Google auth initialization error:', error);
      toast.error('Failed to initialize Google authentication: ' + error.message);
      setLoading(false);
    }
  };

  const handleGoogleCallback = (response) => {
    if (response.code) {
      setLoading(true);

      // Send authorization code to our backend for processing
      const googleData = {
        code: response.code,
      };

      googleAuthMutation.mutate(googleData, {
        onSuccess: (data) => {
          if (data.token) {
            localStorage.setItem('authToken', data.token);
          }
          if (data.user) {
            dispatch(loginAction({ user: data.user, token: data.token }));
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          toast.success('Google authentication successful!');
          onClose();
          setFormData({
            email: '',
            mobile: '',
            password: '',
            name: '',
            secMobile: '',
            fullAddress: '',
            landmark: '',
            city: '',
            postalCode: '',
            country: 'India',
            profileImg: '',
          });
          setLoading(false);
        },
        onError: (error) => {
          setError(error.response?.data?.message || 'Google authentication failed');
          setLoading(false);
        },
      });
    } else {
      toast.error('No authorization code received from Google');
      setLoading(false);
    }
  };

  const formConfig = {
    login: [
      {
        label: 'Email Address',
        type: 'email',
        name: 'email',
        required: true,
        value: formData.email,
        placeholder: 'Enter your email',
      },
      {
        label: 'Password',
        type: 'text',
        name: 'password',
        required: true,
        value: formData.password,
        placeholder: 'Enter your password',
      },
    ],
    forgot:
      forgotStep === 'email'
        ? [
            {
              label: 'Email Address',
              type: 'email',
              name: 'email',
              required: true,
              value: formData.email,
              placeholder: 'Enter your email',
            },
          ]
        : forgotStep === 'otp'
          ? [
              {
                label: 'OTP',
                type: 'text',
                name: 'otp',
                required: true,
                value: formData.otp,
                placeholder: 'Enter the 6-digit code',
              },
            ]
          : [
              {
                label: 'New Password',
                type: 'text',
                name: 'newPassword',
                required: true,
                value: formData.newPassword,
                placeholder: 'Enter your new password',
              },
              {
                label: 'Confirm New Password',
                type: 'text',
                name: 'confirmNewPassword',
                required: true,
                value: formData.confirmNewPassword,
                placeholder: 'Confirm your new password',
              },
            ],
    signup: {
      basic: [
        {
          label: 'Full Name',
          type: 'text',
          name: 'name',
          required: true,
          value: formData.name,
          placeholder: 'Enter your full name',
        },
        {
          label: 'Email Address',
          type: 'email',
          name: 'email',
          required: true,
          value: formData.email,
          placeholder: 'Enter your email',
        },
      ],
      contact: [
        {
          label: 'Mobile',
          type: 'text',
          name: 'mobile',
          required: true,
          value: formData.mobile,
          placeholder: 'Enter your mobile number',
        },
        {
          label: 'Secondary Mobile (Optional)',
          type: 'text',
          name: 'secMobile',
          required: false,
          value: formData.secMobile,
          placeholder: 'Enter secondary mobile number',
        },
      ],
      address: [
        {
          label: 'Full Address',
          type: 'text',
          name: 'fullAddress',
          required: false,
          value: formData.fullAddress,
          placeholder: 'Enter your full address',
          rows: '3',
        },
        {
          label: 'Landmark',
          type: 'text',
          name: 'landmark',
          required: false,
          value: formData.landmark,
          placeholder: 'Enter nearby landmark',
        },
        {
          label: 'City',
          type: 'select',
          name: 'city',
          required: false,
          value: formData.city || '',
          placeholder: citiesLoading ? 'Loading cities…' : 'Select your city',
          options: cityOptions,
          disabled: false,
        },
        {
          label: 'Postal Code',
          type: 'text',
          name: 'postalCode',
          required: false,
          value: formData.postalCode,
          placeholder: 'Enter postal code',
        },
        {
          label: 'Country',
          type: 'select',
          name: 'country',
          required: false,
          value: formData.country,
          placeholder: 'Select your country',
          options: [
            { label: 'India', value: 'India' },
            { label: 'USA', value: 'USA' },
            { label: 'UK', value: 'UK' },
            { label: 'Canada', value: 'Canada' },
            { label: 'Australia', value: 'Australia' },
            { label: 'Other', value: 'Other' },
          ],
        },
      ],
      profile: [
        {
          label: 'Profile Image URL',
          type: 'url',
          name: 'profileImg',
          required: false,
          value: formData.profileImg,
          placeholder: 'Enter profile image URL',
        },
      ],
      password: [
        {
          label: 'Password',
          type: 'text',
          name: 'password',
          required: true,
          value: formData.password,
          placeholder: 'Enter your password',
        },
        {
          label: 'Confirm Password',
          type: 'text',
          name: 'confirmPassword',
          required: true,
          value: formData.confirmPassword,
          placeholder: 'Confirm your password',
        },
      ],
    },
  };

  // Helper function to render form fields
  const renderFormFields = (fields) => {
    return fields.map((field, index) => (
      <div key={index} className={styles.formGroup}>
        <Input.Root name={field.name} required={field.required}>
          <Input.Label>{field.label}</Input.Label>
          <Input.Control
            type={field.type}
            value={field.value}
            onChange={handleInputChange}
            placeholder={field.placeholder}
            rows={field.rows}
            options={field.options}
            disabled={field.disabled}
          />
        </Input.Root>
      </div>
    ));
  };

  // Helper function to render form section
  const renderFormSection = (title, fields) => (
    <div className={styles.formSection}>
      <h3 className={styles.formSectionTitle}>{title}</h3>
      {renderFormFields(fields)}
    </div>
  );
  return createPortal(
    <div className={styles.authOverlay} role="presentation" onClick={onClose}>
      <div
        className={styles.authModalPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.authModalContent}>
          <button
            type="button"
            className={styles.authModalClose}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>

          <div className={styles.authHeader}>
            <BrandLogo variant="banner" tone="onLight" className={styles.authBrand} />
            <h2 id="auth-modal-title">
              {authType === 'signin' && 'Welcome Back'}
              {authType === 'signup' && 'Join B4BARBER'}
              {authType === 'forgot' && 'Reset Password'}
            </h2>
            <p>
              {authType === 'signin' && 'Sign in to your account'}
              {authType === 'signup' && 'Create your account'}
              {authType === 'forgot' &&
                (forgotStep === 'email'
                  ? 'Enter your email to receive a reset code'
                  : forgotStep === 'otp'
                    ? `Enter the code sent to ${formData.email}`
                    : 'Choose a new password')}
            </p>
          </div>

          <form className={styles.authForm} onSubmit={handleSubmit}>
            {authType === 'signup' && (
              <>{renderFormSection('Basic Information', formConfig.signup.basic)}</>
            )}

            {authType === 'signin' && !useMobile && <>{renderFormFields(formConfig.login)}</>}

            {authType === 'forgot' && <>{renderFormFields(formConfig.forgot)}</>}

            {authType === 'signup' && (
              <>{renderFormSection('Contact Information', formConfig.signup.contact)}</>
            )}

            {authType === 'signup' && (
              <>
                {renderFormSection('Address Information (Optional)', formConfig.signup.address)}
                {renderFormSection('Profile Information (Optional)', formConfig.signup.profile)}
              </>
            )}

            {authType === 'signup' && <>{renderFormFields(formConfig.signup.password)}</>}

            {error && <div className={styles.authError}>{error}</div>}
            <Button
              fullWidth
              type="submit"
              variant="primary"
              className={styles.authSubmit}
              disabled={loading}
              size="sm"
            >
              {authType === 'signin' && (loading ? 'Signing in...' : 'Sign In')}
              {authType === 'signup' && (loading ? 'Creating account...' : 'Create Account')}
              {authType === 'forgot' &&
                (forgotStep === 'email'
                  ? loading
                    ? 'Sending...'
                    : 'Send OTP'
                  : forgotStep === 'otp'
                    ? loading
                      ? 'Verifying...'
                      : 'Verify OTP'
                    : loading
                      ? 'Resetting...'
                      : 'Reset Password')}
            </Button>
          </form>

          {authType !== 'forgot' && (
            <>
              <div className={styles.authDivider}>
                <span>or</span>
              </div>

              <Button
                size="sm"
                variant="ghost"
                border
                fullWidth
                onClick={handleGoogleAuth}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {loading ? 'Authenticating...' : 'Continue with Google'}
              </Button>
            </>
          )}

          <div className={styles.authFooter}>
            {authType === 'signin' && (
              <>
                <p>
                  Don't have an account?{' '}
                  <button className={styles.authLink} onClick={() => setAuthType('signup')}>
                    Sign up
                  </button>
                </p>
                <button
                  className={`${styles.authLink} ${styles.forgotLink}`}
                  onClick={() => setAuthType('forgot')}
                >
                  Forgot password?
                </button>
              </>
            )}

            {authType === 'signup' && (
              <p>
                Already have an account?{' '}
                <button className={styles.authLink} onClick={() => setAuthType('signin')}>
                  Sign in
                </button>
              </p>
            )}

            {authType === 'forgot' && forgotStep === 'otp' && (
              <p>
                Didn't get a code?{' '}
                <button
                  className={styles.authLink}
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    setLoading(true);
                    forgotPasswordMutation.mutate(
                      { email: formData.email },
                      {
                        onSuccess: () => {
                          toast.success('OTP resent to your email.');
                          setLoading(false);
                        },
                        onError: (err) => {
                          setError(err.response?.data?.error || 'Could not resend OTP.');
                          setLoading(false);
                        },
                      }
                    );
                  }}
                >
                  Resend
                </button>
              </p>
            )}

            {authType === 'forgot' && (
              <p>
                Remember your password?{' '}
                <button className={styles.authLink} onClick={() => setAuthType('signin')}>
                  Sign in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserNavbar;
